/**
 * The Owner's review drafts from Day 4 onward: the pasted import and
 * annotation JSON, and the decision JSON the page prefills toward
 * skepticism once candidates exist — audience signals approved, findings,
 * opportunities and directions rejected until the Owner says otherwise.
 */

import { useEffect, useState } from 'react';

import type { AsyncState } from '../common.js';
import type { AnalysisRunDetail } from './analysis-run-detail.js';

export type ReviewDrafts = ReturnType<typeof useReviewDrafts>;

export function useReviewDrafts(state: AsyncState<AnalysisRunDetail>) {
  const [contentImportJson, setContentImportJson] = useState('');
  const [ownerSelectionJson, setOwnerSelectionJson] = useState('');
  const [deepAnnotationJson, setDeepAnnotationJson] = useState('');
  const [deepReviewJson, setDeepReviewJson] = useState('');
  const [audienceReviewJson, setAudienceReviewJson] = useState('');
  const [findingsReviewJson, setFindingsReviewJson] = useState('');
  const [opportunityReviewJson, setOpportunityReviewJson] = useState('');
  const [directionReviewJson, setDirectionReviewJson] = useState('');

  useEffect(() => {
    if (state.status !== 'ready' || audienceReviewJson !== '') return;
    const proposals = state.value.day6.signals.filter(
      (signal) => signal.reviewState === 'PROPOSED',
    );
    if (proposals.length === 0) return;
    setAudienceReviewJson(
      JSON.stringify(
        {
          decisions: proposals.map((signal) => ({
            signalVersionId: signal.id,
            decision: 'APPROVED',
          })),
        },
        null,
        2,
      ),
    );
  }, [audienceReviewJson, state]);

  useEffect(() => {
    if (state.status !== 'ready' || findingsReviewJson !== '') return;
    const candidates = state.value.day7.candidates;
    if (candidates.length === 0) return;
    setFindingsReviewJson(
      JSON.stringify(
        {
          decisions: candidates.map((candidate) => {
            const eligibility = candidate.payload['eligibility'];
            const stateValue =
              typeof eligibility === 'object' &&
              eligibility !== null &&
              !Array.isArray(eligibility)
                ? (eligibility as Record<string, unknown>)['state']
                : null;
            return {
              candidateArtifactId: candidate.id,
              decision:
                stateValue === 'ELIGIBLE' ? 'REJECTED' : 'INSUFFICIENT_EVIDENCE',
              reason:
                stateValue === 'ELIGIBLE'
                  ? 'Owner decision required after inspecting support, counter-evidence and limitations.'
                  : 'The deterministic eligibility checks found insufficient support.',
            };
          }),
        },
        null,
        2,
      ),
    );
  }, [findingsReviewJson, state]);

  useEffect(() => {
    if (state.status !== 'ready' || opportunityReviewJson !== '') return;
    const candidates = state.value.day8.opportunityCandidates;
    if (candidates.length === 0) return;
    let nextOrder = 1;
    setOpportunityReviewJson(
      JSON.stringify(
        {
          decisions: candidates.map((candidate) => {
            const validity = candidate.payload['validity'];
            const reviewable = validity === 'ELIGIBLE' || validity === 'CONDITIONAL';
            const decision = reviewable ? 'REJECTED' : 'INSUFFICIENT_EVIDENCE';
            const result = {
              candidateArtifactId: candidate.id,
              decision,
              reason: reviewable
                ? 'Owner decision required after inspecting target fit, gates, constraints, safety and adaptation risk.'
                : 'The deterministic target-fit gates did not support this opportunity.',
              ...(reviewable ? { order: nextOrder } : {}),
            };
            if (reviewable) nextOrder += 1;
            return result;
          }),
        },
        null,
        2,
      ),
    );
  }, [opportunityReviewJson, state]);

  useEffect(() => {
    if (state.status !== 'ready' || directionReviewJson !== '') return;
    const candidates = state.value.day8.creativeDirectionCandidates;
    if (candidates.length === 0) return;
    setDirectionReviewJson(
      JSON.stringify(
        {
          decisions: candidates.map((candidate, index) => ({
            candidateArtifactId: candidate.id,
            decision: 'REJECTED',
            reason:
              'Owner decision required after inspecting target relevance, support, proof, feasibility, safety and copying risk.',
            order: index + 1,
          })),
        },
        null,
        2,
      ),
    );
  }, [directionReviewJson, state]);

  return {
    contentImportJson,
    setContentImportJson,
    ownerSelectionJson,
    setOwnerSelectionJson,
    deepAnnotationJson,
    setDeepAnnotationJson,
    deepReviewJson,
    setDeepReviewJson,
    audienceReviewJson,
    setAudienceReviewJson,
    findingsReviewJson,
    setFindingsReviewJson,
    opportunityReviewJson,
    setOpportunityReviewJson,
    directionReviewJson,
    setDirectionReviewJson,
  };
}
